import Axios from "axios";

/**
 * One-way latch for "this session is over, and here is why".
 *
 * The signal can arrive from several places at once — an in-flight API call,
 * the session poll, a failed refresh, the idle timer — so it is held here
 * rather than in any one component. The first reason wins and later signals
 * are ignored, so the user cannot be shown a stack of dialogs, and cannot be
 * told "logged in somewhere else" for what was really an idle timeout.
 */

/** The code the portal server sends for a live-but-ended session. */
export const SESSION_ENDED = "SESSION_ENDED";

export type SessionEndReason =
  /** Another login displaced this one (the portal is registered singleSession). */
  | "displaced"
  /** The token ran out — usually the machine slept through a refresh. */
  | "expired"
  /** Signed out after the inactivity timeout elapsed. */
  | "idle";

type Listener = (reason: SessionEndReason) => void;

let endedReason: SessionEndReason | null = null;
const listeners = new Set<Listener>();

export const isSessionEnded = () => endedReason !== null;
export const getSessionEndReason = () => endedReason;

export const onSessionEnded = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Raise the signal. The token is dropped immediately so nothing can keep
 * using it, but the redirect is left to the dialog, which shows the reason,
 * counts down and then goes. The point is that the user sees WHY before the
 * page changes under them — not that they have to click.
 *
 * Callers that still need the token afterwards (the idle sign-out has to POST
 * a logout) must capture it into a local before calling this.
 */
export const signalSessionEnded = (reason: SessionEndReason) => {
  if (endedReason) return;
  endedReason = reason;
  try {
    localStorage.removeItem("token");
  } catch {
    /* storage blocked — the in-memory clear below still applies */
  }
  // Also drop the default header. Without this, background requests keep
  // sending a dead token; each 401 lands in the never-settling promise in
  // api-caller and leaks a pending closure while the dialog sits open.
  delete Axios.defaults.headers.common["Authorization"];
  listeners.forEach((listener) => listener(reason));
};

/** Called after the user acknowledges, on the way to the login page. */
export const resetSessionEnded = () => {
  endedReason = null;
};
